import React, { useMemo, useRef, useState } from 'react';
import { Text } from '../../../../style-utils/text';
import { palette } from '../../../../style-utils/theme';
import { RightArrow } from '../../../icons/right-arrow';
import {
  ButtonsContainer,
  ButtonTitle,
  CancelButton,
  ErrorNotice,
  FormBody,
  FormHeader,
  HelperText,
  InputContainer,
  Label,
  ModalBackground,
  PanelNote,
  PortfolioFormCard,
  SaveButton,
  StyledPortfolioSelect,
} from './dashboard-portfolios.styled';
import { StatusLabel, scrimMotion, useOverlayMotion } from '../../../../motion';
import {
  addBuildingsToPortfolio,
  HierarchyApiError,
  moveBuildingToPortfolio,
} from '../../../../api/hierarchy-client';
import {
  AccessDeltaMember,
  Portfolio,
} from '../../../../interfaces/hierarchy-interfaces';
import { normalizeRole, roleLabel } from '../../../../auth/roles';
import { usePortfolios } from '../../../hooks/use-portfolios';
import { useDialogFocus } from '../../../hooks/use-dialog-focus';
import { ModalPortal } from '../../../modal/modal-portal';
// Shared with the move-IN modal: both post to POST /portfolios/:id/buildings,
// so both live under the same server-side batch cap.
import { BULK_MOVE_MAX } from './dashboard-portfolio-add-buildings';

interface DashboardPortfolioMoveBuildingProps {
  // The portfolio the buildings are in right now. Never offered as a
  // destination — a move to where they already are is a no-op the server
  // answers with moved:false.
  source: Portfolio;
  // One id or many. A building belongs to exactly ONE portfolio and the column
  // is NOT NULL, so "take these out of `source`" and "put these into the
  // destination" are the same single act.
  buildingIds: number[];
  // Named only when a single building is moving; null for a selection, where
  // the count is what the user is tracking.
  buildingName: string | null;
  onCancel: () => void;
  // Both ends of the move, so the caller can drop its cached detail for the
  // portfolio the buildings left as well as the one they landed in.
  onMoved: (affectedPortfolioIds: number[]) => void;
}

const buildingLabel = (count: number): string =>
  count === 1 ? '1 building' : `${count} buildings`;

const peopleLabel = (count: number): string =>
  count === 1 ? '1 person' : `${count} people`;

// The server returns the raw column value; users.role is one of exactly three
// strings (chk_users_role) and null for someone holding no staff role. The label
// comes from the shared table rather than a local map — the local one had a
// `|| member.role` fallback that printed "property_manager" verbatim for any
// spelling it did not list.
const memberLabel = (member: AccessDeltaMember): string => {
  const name = member.name || 'A team member';
  const role = normalizeRole(member.role) ? roleLabel(member.role, { short: true }) : null;

  return role ? `${name} (${role})` : name;
};

// Named, not counted, while the list is short: "Maria lost access" is actionable
// and "2 people lost access" is not. Past three the names stop fitting in a
// modal and the count is the honest summary.
const NAMED_MAX = 3;

const membersSentence = (members: AccessDeltaMember[]): string =>
  members.length > NAMED_MAX
    ? peopleLabel(members.length)
    : members.map(memberLabel).join(', ');

// What the move ACTUALLY did to who can see what. Held after a successful move
// instead of closing straight away: the delta only exists once the move has
// happened — trg_building_changed re-expands the grants as part of the same
// statement — so this is a confirmation after the fact with an Undo, not a
// preview before it.
interface MoveOutcome {
  destinationId: number;
  movedIds: number[];
  scopeEnforced: boolean;
  gained: AccessDeltaMember[];
  lost: AccessDeltaMember[];
}

export const DashboardPortfolioMoveBuilding: React.FC<
  DashboardPortfolioMoveBuildingProps
> = ({ source, buildingIds, buildingName, onCancel, onMoved }) => {
  // Defaults to ACTIVE portfolios, which is exactly the destination set: the
  // server refuses to move a building into an archived one.
  const { portfolios, isLoading, error, allowed } = usePortfolios();

  const [destinationId, setDestinationId] = useState<string>('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [outcome, setOutcome] = useState<MoveOutcome | null>(null);
  const savingRef = useRef(false);

  // Escape closes the dialog, and once the move has happened closing it is
  // "keep the move" — not a cancel. Both ends of the move are still refreshed,
  // so the list behind never shows the pre-move state.
  const dismiss = () => {
    if (outcome) {
      onMoved([source.id, outcome.destinationId]);

      return;
    }

    onCancel();
  };

  const dialogRef = useDialogFocus<HTMLFormElement>(true, () => {
    if (!savingRef.current) {
      dismiss();
    }
  });

  const destinations = useMemo(
    () => portfolios.filter(portfolio => portfolio.id !== source.id),
    [portfolios, source.id]
  );

  const count = buildingIds.length;
  const isSingle = count === 1;
  // "Move buildings…" ticks every building in the portfolio, which is exactly
  // how a >100-building portfolio ends up over the server's batch cap. Refusing
  // here — rather than letting the submit come back a 400 — keeps the limit
  // visible while there is still something the owner can do about it.
  const isOverCap = count > BULK_MOVE_MAX;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (savingRef.current || outcome || destinationId === '' || isOverCap) {
      return;
    }

    const targetId = Number(destinationId);

    setSubmitError(null);
    savingRef.current = true;
    setIsSaving(true);

    try {
      let next: MoveOutcome;

      if (isSingle) {
        const moved = await moveBuildingToPortfolio(buildingIds[0], targetId);

        next = {
          destinationId: targetId,
          movedIds: moved.moved ? [buildingIds[0]] : [],
          scopeEnforced: moved.scope_enforced,
          gained: moved.gained,
          lost: moved.lost,
        };
      } else {
        // Atomic server-side: either every named building lands in the
        // destination or none does, so a half-moved selection is not a state
        // this screen can end up in.
        const moved = await addBuildingsToPortfolio(targetId, buildingIds);

        next = {
          destinationId: targetId,
          // The buildings that actually changed hands, which is what an Undo
          // has to send back — one already sitting in the destination was never
          // written and must not be dragged into the source portfolio.
          movedIds: moved.moves.map(entry => entry.building_id),
          scopeEnforced: moved.scope_enforced,
          gained: moved.gained,
          lost: moved.lost,
        };
      }

      savingRef.current = false;

      // Only stop to report something there is something to report. With scope
      // enforcement off the access rows are accurate but never consulted, so
      // "someone lost access" would be a false sentence — and with an empty
      // delta there is nothing to confirm. Both cases close as before.
      if (next.scopeEnforced && (next.gained.length || next.lost.length)) {
        setOutcome(next);
        setIsSaving(false);

        return;
      }

      onMoved([source.id, targetId]);
    } catch (caught) {
      // The server's wording carries what a generic string cannot — the name of
      // the portfolio that turned out to be archived, the building it choked
      // on. Show it verbatim.
      const message =
        caught instanceof HierarchyApiError
          ? caught.message
          : 'Something went wrong. Please try again.';

      setSubmitError(message);
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  // Undo is a second move, not a rollback: the same endpoint, the other way
  // round. trg_building_changed re-expands in both directions (proven), so the
  // access delta reverses itself — and the audit log honestly shows two moves,
  // which is what actually happened.
  const handleUndo = async () => {
    if (savingRef.current || !outcome) {
      return;
    }

    setSubmitError(null);
    savingRef.current = true;
    setIsSaving(true);

    try {
      if (outcome.movedIds.length === 1) {
        await moveBuildingToPortfolio(outcome.movedIds[0], source.id);
      } else if (outcome.movedIds.length > 1) {
        await addBuildingsToPortfolio(source.id, outcome.movedIds);
      }

      savingRef.current = false;
      onMoved([source.id, outcome.destinationId]);
    } catch (caught) {
      const message =
        caught instanceof HierarchyApiError
          ? caught.message
          : 'The move could not be undone. Move it back from the portfolio it is in now.';

      setSubmitError(message);
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const hasDestinations = destinations.length > 0;
  // What moved, phrased once, for both the heading and the delta sentences.
  const movedSubject = isSingle
    ? buildingName || 'This building'
    : buildingLabel(count);

  const modalMotion = useOverlayMotion('modal');

  return (
    <ModalPortal>
      <ModalBackground
        {...scrimMotion}
        id="portfolio-move-background"
        onMouseDown={() => {
          if (!savingRef.current) {
            dismiss();
          }
        }}
      >
        <PortfolioFormCard
          {...modalMotion}
          ref={dialogRef}
          tabIndex={-1}
          id="portfolio-move-form"
          onMouseDown={event => event.stopPropagation()}
          onSubmit={handleSubmit}
          role="dialog"
          aria-modal="true"
          aria-labelledby="portfolio-move-heading"
          noValidate
        >
          <FormHeader>
            <Text
              id="portfolio-move-heading"
              mt={0}
              mb={0}
              fontSize={20}
              fontWeight={700}
              color={palette.ink}
            >
              {outcome
                ? 'Moved — access changed'
                : isSingle
                ? 'Move building'
                : `Move ${buildingLabel(count)}`}
            </Text>
            <Text mt={0} mb={0} fontSize={13} color={palette.slate}>
              {outcome ? (
                <>
                  Staff see buildings through the portfolio they are assigned to,
                  so this move changed who can see {movedSubject}.
                </>
              ) : (
                <>
                  A building belongs to exactly one portfolio, so this moves{' '}
                  {isSingle ? buildingName || 'it' : 'them'} out of {source.name}.
                </>
              )}
            </Text>
          </FormHeader>

          <FormBody>
            <ErrorNotice message={submitError} />

            <ErrorNotice message={error} />

            {outcome ? (
              <>
                {outcome.lost.length > 0 && (
                  <PanelNote>
                    <strong>No longer sees {movedSubject}:</strong>{' '}
                    {membersSentence(outcome.lost)}.
                  </PanelNote>
                )}
                {outcome.gained.length > 0 && (
                  <PanelNote>
                    <strong>Now sees {movedSubject}:</strong>{' '}
                    {membersSentence(outcome.gained)}.
                  </PanelNote>
                )}
                <HelperText>
                  Undo moves {isSingle ? 'it' : 'them'} straight back to{' '}
                  {source.name} and reverses this.
                </HelperText>
              </>
            ) : !allowed ? (
              <PanelNote>
                You do not have access to the portfolio list, so there is nowhere
                to move this from here.
              </PanelNote>
            ) : (
              <InputContainer>
                <Label htmlFor="portfolio-move-destination">Move to</Label>
                <StyledPortfolioSelect
                  id="portfolio-move-destination"
                  name="destination"
                  value={destinationId}
                  disabled={isSaving || isLoading || !hasDestinations}
                  onChange={event => setDestinationId(event.target.value)}
                >
                  <option value="">
                    {isLoading ? 'Loading portfolios…' : 'Choose a portfolio'}
                  </option>
                  {destinations.map(portfolio => (
                    <option key={portfolio.id} value={String(portfolio.id)}>
                      {portfolio.is_default
                        ? `${portfolio.name} (default)`
                        : portfolio.name}
                    </option>
                  ))}
                </StyledPortfolioSelect>
                {/* Was "restore one first" — a remedy R-20 removed with the
                    Restore control. */}
                <HelperText>
                  Archived portfolios are not offered — choose an active one.
                </HelperText>
              </InputContainer>
            )}

            {!outcome && allowed && !isLoading && !hasDestinations && (
              <PanelNote>
                {source.name} is your only active portfolio. Add another one, then
                move {isSingle ? 'this building' : 'these buildings'} into it.
              </PanelNote>
            )}

            {!outcome && isOverCap && (
              <PanelNote>
                A single move carries at most {BULK_MOVE_MAX} buildings, and{' '}
                {buildingLabel(count)} are selected. Close this and deselect{' '}
                {buildingLabel(count - BULK_MOVE_MAX)} to continue.
              </PanelNote>
            )}
          </FormBody>

          <ButtonsContainer>
            {outcome ? (
              <>
                <CancelButton
                  type="button"
                  disabled={isSaving}
                  aria-busy={isSaving}
                  onClick={handleUndo}
                >
                  <StatusLabel
                    state={isSaving ? 'busy' : 'idle'}
                    labels={{
                      idle: <ButtonTitle>Undo the move</ButtonTitle>,
                      busy: <ButtonTitle>Undoing…</ButtonTitle>,
                    }}
                  />
                </CancelButton>
                {/* The arrow stays: it used to drop out while saving and the
                    button changed width beside the one being pressed. */}
                <SaveButton type="button" disabled={isSaving} onClick={dismiss}>
                  <ButtonTitle>Keep the move</ButtonTitle>
                  <RightArrow />
                </SaveButton>
              </>
            ) : (
              <>
                <CancelButton
                  type="button"
                  disabled={isSaving}
                  onClick={onCancel}
                >
                  <ButtonTitle>Cancel</ButtonTitle>
                </CancelButton>
                <SaveButton
                  type="submit"
                  disabled={isSaving || destinationId === '' || isOverCap}
                  aria-busy={isSaving}
                  title={
                    isOverCap
                      ? `A single move carries at most ${BULK_MOVE_MAX} buildings.`
                      : destinationId === ''
                      ? 'Choose the portfolio to move to first.'
                      : undefined
                  }
                >
                  <StatusLabel
                    state={isSaving ? 'busy' : 'idle'}
                    labels={{
                      idle: (
                        <>
                          <ButtonTitle>
                            {isSingle
                              ? 'Move building'
                              : `Move ${buildingLabel(count)}`}
                          </ButtonTitle>
                          <RightArrow />
                        </>
                      ),
                      busy: <ButtonTitle>Moving…</ButtonTitle>,
                    }}
                  />
                </SaveButton>
              </>
            )}
          </ButtonsContainer>
        </PortfolioFormCard>
      </ModalBackground>
    </ModalPortal>
  );
};
